import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, ChevronLeft, ChevronRight, Layers, Filter, X, RefreshCw, Box, ArrowUpDown, ChevronsLeft, ChevronsRight } from 'lucide-react';
import api from './axiosConfig';
import toast from 'react-hot-toast';
import { usePermissions } from './usePermissions';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة

const AssetCategoryList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canCreate, canUpdate, canDelete } = usePermissions('asset-categories');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // URL Parameters (Persistence)
  const page = parseInt(searchParams.get('page')) || 1;
  const limit = 50;
  const q = searchParams.get('q') || "";
  const sortBy = searchParams.get('sortBy') || "category_id";
  const sortDir = searchParams.get('sortDir') || "desc";

  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionOrg = session.org_id || '';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/asset-categories', {
        params: { page, limit, q, org_id: sessionOrg, sortBy, sortDir }
      });
      setData(res.data.data);
      setTotalCount(res.data.total);
    } catch (err) { 
      toast.error(t('common.load_error', "Failed to load categories")); 
    } finally { 
      setLoading(false); 
    }
  }, [page, q, sortBy, sortDir, sessionOrg, t]);

  useEffect(() => {
    const timer = setTimeout(() => fetchData(), 100);
    return () => clearTimeout(timer);
  }, [fetchData]);

  const updateFilters = (newFilters) => {
    const params = Object.fromEntries(searchParams.entries());
    const updated = { ...params, ...newFilters };
    Object.keys(updated).forEach(key => !updated[key] && delete updated[key]);
    setSearchParams(updated);
  };

  const handleSort = (column) => {
    const direction = sortBy === column && sortDir === 'asc' ? 'desc' : 'asc';
    updateFilters({ sortBy: column, sortDir: direction, page: 1 });
  };

  const handleRowClick = (id) => {
    const query = searchParams.toString();
    navigate(`/asset-categories/${id}${query ? '?' + query : ''}`); // 🎯 تمرير الكويري
  };

  const totalPages = Math.ceil(totalCount / limit) || 1;

  return (
    <div className={`flex flex-col h-full bg-surface-app text-[11px] uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo' : 'font-sans'}`} dir={isRTL ? 'rtl' : 'ltr'}>

      {/* --- Header Toolbar --- */}
      <div className="bg-surface-card p-3 border-b shadow-sm z-20">
        <div className="flex justify-between items-center px-4">
          <h2 className="text-primary-dark font-black italic text-lg flex items-center gap-2">
            <Layers size={22} className="text-status-warning" />
            {t('asset_categories.list.title', 'Asset Categories')}
            <RefreshCw
              size={18}
              className={`cursor-pointer transition-all ${loading ? 'animate-spin text-status-warning' : 'text-primary'} ${isRTL ? 'mr-2' : 'ml-2'}`}
              onClick={() => fetchData()}
            />
          </h2>

          <div className="flex gap-3">
            <button onClick={() => setSearchParams({})} className="flex items-center gap-2 px-4 py-2 text-ink-secondary hover:text-status-danger font-bold transition-colors">
              <X size={16}/> {t('common.clear_filters', 'CLEAR FILTERS')}
            </button>
            {canCreate && (
              <button onClick={() => navigate('/asset-categories/new')} className="bg-primary-dark text-white px-8 py-2 rounded-sm font-black flex items-center gap-2 hover:bg-primary shadow-lg transition-all">
                <Plus size={16} /> {t('asset_categories.list.new_category', 'NEW CATEGORY')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* --- Table Area --- */}
      <div className="flex-1 overflow-auto p-4 custom-scrollbar">
        <div className="bg-surface-card border rounded-sm shadow-sm overflow-hidden">
          <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
            <thead className="bg-primary-dark text-white font-bold uppercase sticky top-0 z-10">
              <tr>
                <SortableHeader label={t('asset_categories.fields.category_id', 'Category Code')} sortKey="category_id" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('asset_categories.fields.description', 'Description')} sortKey="description" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('asset_categories.fields.dep_method', 'Dep. Method')} sortKey="depreciation_method" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
                <SortableHeader label={t('asset_categories.fields.life', 'Life (Yrs)')} sortKey="useful_life_years" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
              </tr>
              {/* Filter Row */}
              <tr className="bg-surface-panel">
                <td colSpan="2" className="p-1 border-x border-line">
                  <div className={`relative flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Search className={`absolute text-ink-secondary ${isRTL ? 'right-2' : 'left-2'}`} size={14} />
                    <input
                      className={`w-full p-1.5 text-primary font-bold outline-none border border-line-subtle focus:border-primary bg-surface-card ${isRTL ? 'pr-8' : 'pl-8'}`}
                      placeholder={t('asset_categories.list.search', 'Search Categories...')}
                      value={q}
                      onChange={e => updateFilters({ q: e.target.value, page: 1 })}
                    />
                  </div>
                </td>
                <td className="p-1 border-x border-line bg-surface-panel"></td>
                <td className="p-1 border-x border-line bg-surface-panel"></td>
              </tr>
            </thead>

            <tbody className="divide-y divide-line-subtle italic font-bold">
              {loading ? (
                <tr><td colSpan="4" className="p-20 text-center font-black text-ink-secondary animate-pulse tracking-[0.2em]">{t('common.syncing', 'Syncing Data...')}</td></tr>
              ) : data.length > 0 ? (
                data.map((item) => (
                  <tr key={item.id} className="hover:bg-primary-soft cursor-pointer transition-colors group" onClick={() => handleRowClick(item.id)}>
                    <td className="p-3 border-x border-line-subtle font-black text-primary group-hover:underline">{item.category_id}</td>
                    <td className="p-3 border-x border-line-subtle text-ink">{item.description}</td>
                    <td className="p-3 border-x border-line-subtle text-center">
                      <span className="bg-status-warning-bg text-status-warning px-2 py-0.5 rounded-full text-[9px] shadow-sm border border-status-warning-bg">
                        {item.depreciation_method}
                      </span>
                    </td>
                    <td className="p-3 border-x border-line-subtle text-center text-ink-secondary font-black">{item.useful_life_years}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="4" className="p-20 text-center font-black text-ink-secondary italic uppercase">{t('asset_categories.list.no_records', 'No Categories Found')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Pagination Footer --- */}
      <div className={`bg-primary-dark text-white p-2 px-8 flex justify-between items-center shadow-inner z-30 font-black text-[10px] ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Box size={14} className="text-primary"/>
                <span>{t('common.showing', 'Showing')}: <span className="text-primary">{(page-1)*limit+1}-{Math.min(page*limit, totalCount)}</span></span>
            </div>
            <div className={`flex items-center gap-2 border-white/20 ${isRTL ? 'border-r pr-6 flex-row-reverse' : 'border-l pl-6'}`}>
                <Filter size={14} className="text-status-warning"/>
                <span>{t('common.total', 'Total')}: <span className="text-status-warning">{totalCount}</span></span>
            </div>
        </div>

        <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-1 bg-black/20 rounded px-2 py-1 border border-white/10 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <button disabled={page === 1} onClick={() => updateFilters({page: 1})} className="p-1.5 hover:text-status-warning disabled:opacity-20">
                    <ChevronsLeft size={18} className={isRTL ? 'rotate-180' : ''}/>
                </button>
                <button disabled={page === 1} onClick={() => updateFilters({page: page - 1})} className="p-1.5 hover:text-status-warning disabled:opacity-20">
                    <ChevronLeft size={18} className={isRTL ? 'rotate-180' : ''}/>
                </button>
                <div className="px-4 text-[11px] font-mono border-x border-white/10 mx-1">
                    <span className="text-status-warning text-sm">{page}</span> / {totalPages}
                </div>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: page + 1})} className="p-1.5 hover:text-status-warning disabled:opacity-20">
                    <ChevronRight size={18} className={isRTL ? 'rotate-180' : ''}/>
                </button>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: totalPages})} className="p-1.5 hover:text-status-warning disabled:opacity-20">
                    <ChevronsRight size={18} className={isRTL ? 'rotate-180' : ''}/>
                </button>
            </div>
        </div>
      </div>
    </div>
  );
};

// --- Sortable Header Component ---
const SortableHeader = ({ label, sortKey, sortBy, sortDir, onSort, align = "left", isRTL }) => (
  <th className={`p-3 border-x border-white/10 cursor-pointer hover:bg-primary transition-colors text-${align === 'center' ? 'center' : (isRTL ? 'right' : 'left')}`} onClick={() => onSort(sortKey)}>
      <div className={`flex items-center gap-2 ${align === 'center' ? 'justify-center' : ''} ${isRTL ? 'flex-row-reverse' : ''}`}>
          {label}
          <ArrowUpDown size={12} className={sortBy === sortKey ? 'text-status-warning' : 'text-white/30'} />
      </div>
  </th>
);

export default AssetCategoryList;